import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import { plural } from '@hotjar/webapp/lib/plural';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import {
  HEATMAP_DEVICE_TYPES,
  HEATMAP_WIDTH_PER_DEVICE,
  TOP_CLICKED_REGION_CLASS_PREFIX,
} from '@hotjar/heatmaps';
import { RegionTooltip as HighlightRegionTooltip } from '@hotjar/webapp/features/Highlights/Heatmaps/RegionTooltip';

import type { ClickRegionWithZIndex, ClickRegionPosition } from './helpers';
import { calculateRegionPosition, labelBottom, labelRight } from './helpers';

type RegionProps = {
  position: {
    left: number;
    top: number;
    width: number;
    height: number;
    zIndex: number;
  };
};

const Region = styled.div<RegionProps>`
  position: absolute;
  opacity: 0;
  ${tokens.transition('opacity')};
  background: rgba(0, 158, 253, 0.25);
  border: 1px dashed ${tokens.color.azure};

  &:hover {
    opacity: 1;
  }

  ${({ position }) => `
    left: ${position.left}px;
    top: ${position.top}px;
    width: ${position.width}px;
    height: ${position.height}px;
    z-index: ${position.zIndex};
  `}
`;

type LabelProps = {
  bottom?: boolean;
  right?: boolean;
};

const Label = styled.span<LabelProps>`
  background: ${tokens.color.azure};
  color: ${tokens.color.white};
  ${tokens.text('subtitle2')};
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
  white-space: nowrap;
  left: -1px;
  top: -36px;
  border-radius: ${tokens.radius.m};
  position: absolute;
  pointer-events: none;

  ${({ bottom }) =>
    bottom &&
    `
    top: auto;
    bottom: -36px;
  `}

  ${({ right }) =>
    right &&
    `
    left: auto;
    right: -1px;
  `}
`;

interface ClickRegionProps {
  clickRegion: ClickRegionWithZIndex;
  selectedDeviceType: HeatmapDeviceTypes;
  totalCount: number;
  heatmapWidth: number;
  actualWidth: number;
  onRegionClick: (position: ClickRegionPosition) => void;
  matchingTopClickedElement: number | null;
}

const clickType = (count: number, isDesktop: boolean, t: TFunction) =>
  isDesktop
    ? plural({ one: t('general-terms.click'), other: t('general-terms.clicks') }, count)
    : plural({ one: t('general-terms.tap'), other: t('general-terms.taps') }, count);

const renderNumberOfClicks = (clicksCount: number, isDesktop: boolean, t: TFunction) =>
  `${localeNumber(clicksCount)} ${clickType(clicksCount, isDesktop, t)}`;

const renderPercentage = (clicksCount: number, totalClicks: number) =>
  `${((100 * clicksCount) / totalClicks).toFixed(2)}%`;

export const ClickRegion = ({
  clickRegion,
  selectedDeviceType,
  totalCount,
  heatmapWidth,
  actualWidth,
  onRegionClick,
  matchingTopClickedElement,
}: ClickRegionProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');
  const regionCount = clickRegion[4];
  const numberOfClicks = renderNumberOfClicks(
    regionCount,
    selectedDeviceType === HEATMAP_DEVICE_TYPES.DESKTOP,
    t,
  );
  const percentageOfClicks = renderPercentage(regionCount, totalCount);

  const labelPositionedBottom = labelBottom(clickRegion[1]);
  const labelPositionedRight = labelRight(
    clickRegion[0],
    HEATMAP_WIDTH_PER_DEVICE[selectedDeviceType],
  );

  const position = calculateRegionPosition({
    coords: clickRegion,
    heatmapWidth,
    actualWidth,
    totalClickCount: totalCount,
  });

  return (
    <HighlightRegionTooltip onClick={() => onRegionClick(position)}>
      {(highlightTooltip) => (
        <Region
          position={position}
          className={
            matchingTopClickedElement
              ? `${TOP_CLICKED_REGION_CLASS_PREFIX}${matchingTopClickedElement}`
              : ''
          }
        >
          <Label
            bottom={labelPositionedBottom}
            right={labelPositionedRight}
          >{`${numberOfClicks} (${percentageOfClicks})${highlightTooltip}`}</Label>
        </Region>
      )}
    </HighlightRegionTooltip>
  );
};
